import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { 
  CreditCard, 
  Check, 
  Loader2, 
  Lock,
  Shield,
  Star,
  TrendingUp,
  Bell,
  Download,
  Gift,
  Wallet
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Billing() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isSubscriber, credits, checkAuth, loading: authLoading } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [checkoutDetails, setCheckoutDetails] = useState(null);
  const [pricing, setPricing] = useState({ subscription_price: 9.99, trial_days: 15 });
  const sessionId = searchParams.get('session_id');

  // Fetch pricing from API (public endpoint - no auth required)
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await fetch(`${API_URL}/api/pricing/public`);
        if (response.ok) {
          const data = await response.json();
          setPricing({
            subscription_price: data.subscription_price || 9.99,
            trial_days: data.trial_days || 15,
            referrer_credit_percent: data.referrer_credit_percent || 10,
            referee_credit_percent: data.referee_credit_percent || 10
          });
        }
      } catch (err) {
        console.error('Failed to fetch pricing:', err);
      }
    };
    fetchPricing();
  }, []);

  // Calculate final price after credits
  const subscriptionPrice = pricing.subscription_price || 9.99;
  const trialDays = pricing.trial_days || 15;
  const finalPrice = Math.max(0.01, subscriptionPrice - (credits || 0));
  const creditsApplied = Math.min(credits || 0, subscriptionPrice);

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) return;
    
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Check payment status if returning from Stripe
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [isAuthenticated, authLoading, sessionId, navigate]);

  const pollPaymentStatus = async (sid, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out. Please check your email for confirmation.');
      return;
    }

    setCheckingStatus(true);
    try {
      const response = await fetch(`${API_URL}/api/billing/status/${sid}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.payment_status === 'paid') {
          toast.success('Payment successful! Welcome to Premium!');
          await checkAuth();
          // Clear the session_id from URL
          navigate('/billing', { replace: true });
          return;
        } else if (data.status === 'expired') {
          toast.error('Payment session expired. Please try again.');
          navigate('/billing', { replace: true });
          return;
        }
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch (err) {
      console.error('Error checking payment status:', err);
    } finally {
      if (attempts === 0) setCheckingStatus(false);
    }
  };

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/billing/create-checkout`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({
          origin_url: window.location.origin
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to create checkout session');
      }

      const data = await response.json();
      setCheckoutDetails(data);
      
      // Redirect to Stripe checkout
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: TrendingUp,
      title: 'Detailed Trends',
      description: 'Full historical trend analysis and performance data'
    },
    {
      icon: Star,
      title: 'Head-to-Head Stats',
      description: 'Complete matchup history and seasonal records'
    },
    {
      icon: Bell,
      title: 'Priority Alerts',
      description: 'Early notifications for notable game matchups'
    },
    {
      icon: Download,
      title: 'Data Exports',
      description: 'Export analysis data in CSV and JSON formats'
    }
  ];

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (checkingStatus) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-muted-foreground mx-auto mb-4" />
            <h2 className="font-display text-2xl font-bold uppercase mb-2">Processing Payment</h2>
            <p className="text-muted-foreground">Please wait while we confirm your subscription...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {isSubscriber ? (
            // Subscriber view
            <div className="text-center">
              <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-green-400" />
              </div>
              <h1 className="font-display text-4xl font-bold uppercase tracking-tight mb-4">
                You're Premium!
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                You have full access to all premium features.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
                {features.map((feature, idx) => (
                  <div key={idx} className="bg-card border border-green-500/20 rounded-sm p-4 text-left">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-500/10 rounded-sm flex items-center justify-center">
                        <feature.icon className="w-5 h-5 text-green-400" />
                      </div>
                      <div>
                        <h3 className="font-medium">{feature.title}</h3>
                        <p className="text-xs text-muted-foreground">{feature.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <Button 
                variant="outline" 
                className="mt-8 rounded-sm"
                onClick={() => navigate('/board')}
              >
                Back to Games
              </Button>
              
              {/* Payment History / Download Receipts */}
              <div className="mt-12 max-w-2xl mx-auto">
                <div className="bg-card border border-border rounded-sm p-6">
                  <h3 className="font-display text-lg font-bold uppercase mb-4 flex items-center gap-2">
                    <Download className="w-5 h-5 text-muted-foreground" />
                    Payment History
                  </h3>
                  <ReceiptsList />
                </div>
              </div>
            </div>
          ) : (
            // Non-subscriber view
            <>
              <div className="text-center mb-12">
                <h1 className="font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight mb-4">
                  Unlock Premium
                </h1>
                <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                  Get access to detailed analytics, historical trends, and priority alerts.
                </p>
              </div>

              {/* Pricing Card */}
              <div className="max-w-md mx-auto mb-12">
                <div className="bg-card border-2 border-red-500/30 rounded-sm p-8 relative">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-red-500 text-white text-xs font-bold uppercase px-3 py-1 rounded-sm">
                      Best Value
                    </span>
                  </div>
                  
                  <div className="text-center mb-6">
                    <CreditCard className="w-12 h-12 text-red-400 mx-auto mb-4" />
                    
                    {creditsApplied > 0 ? (
                      <>
                        <div className="flex items-baseline justify-center gap-2 mb-2">
                          <span className="font-display text-3xl font-bold line-through text-muted-foreground">${subscriptionPrice.toFixed(2)}</span>
                          <span className="font-display text-5xl font-bold text-green-400">${finalPrice.toFixed(2)}</span>
                        </div>
                        <span className="text-muted-foreground">CAD/month</span>
                        <div className="mt-3 p-2 bg-green-500/10 border border-green-500/30 rounded-sm">
                          <div className="flex items-center justify-center gap-2 text-green-400 text-sm">
                            <Wallet className="w-4 h-4" />
                            <span>${creditsApplied.toFixed(2)} credit applied!</span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-baseline justify-center gap-2">
                          <span className="font-display text-5xl font-bold">${subscriptionPrice.toFixed(2)}</span>
                          <span className="text-muted-foreground">CAD/month</span>
                        </div>
                        {trialDays > 0 && (
                          <p className="text-sm text-green-400 mt-2">
                            Start with {trialDays}-day free trial
                          </p>
                        )}
                      </>
                    )}
                  </div>

                  <ul className="space-y-3 mb-8">
                    {features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                        <span className="text-sm">{feature.title}</span>
                      </li>
                    ))}
                    <li className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                      <span className="text-sm">Cancel anytime</span>
                    </li>
                  </ul>

                  <Button 
                    onClick={handleSubscribe}
                    disabled={loading}
                    className="w-full btn-blaze rounded-sm font-bold uppercase tracking-wider py-6"
                    data-testid="subscribe-btn"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Lock className="w-5 h-5 mr-2" />
                        {creditsApplied > 0 ? `Pay $${finalPrice.toFixed(2)}` : (trialDays > 0 ? `Start ${trialDays}-Day Free Trial` : 'Subscribe Now')}
                      </>
                    )}
                  </Button>
                  
                  {/* Referral invite */}
                  {(credits || 0) < subscriptionPrice && (
                    <div className="mt-4 text-center">
                      <button 
                        onClick={() => navigate('/referral')}
                        className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center gap-1 mx-auto"
                      >
                        <Gift className="w-4 h-4" />
                        <span>Have a referral code? Earn credits!</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Feature Details */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {features.map((feature, idx) => (
                  <div key={idx} className="bg-card border border-border rounded-sm p-6">
                    <div className="w-12 h-12 bg-secondary rounded-sm flex items-center justify-center mb-4">
                      <feature.icon className="w-6 h-6 text-muted-foreground" />
                    </div>
                    <h3 className="font-display text-lg font-bold uppercase tracking-tight mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                  </div>
                ))}
              </div>

              {/* Security note */}
              <div className="mt-8 p-4 bg-secondary/50 rounded-sm text-center">
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <Shield className="w-4 h-4" />
                  <span>Secure payment powered by Stripe</span>
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}

// Receipts List Component
function ReceiptsList() {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    const fetchReceipts = async () => {
      try {
        const response = await fetch(`${API_URL}/api/billing/my-receipts`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setReceipts(data.receipts || []);
        }
      } catch (err) {
        console.error('Failed to fetch receipts:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchReceipts();
  }, []);

  const handleDownload = async (transactionId) => {
    setDownloadingId(transactionId);
    try {
      const response = await fetch(`${API_URL}/api/billing/receipt/${transactionId}/download`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `receipt-${transactionId.slice(0, 8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Receipt downloaded!');
      } else {
        toast.error('Failed to download receipt');
      }
    } catch (err) {
      toast.error('Download failed. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (receipts.length === 0) {
    return (
      <p className="text-muted-foreground text-sm text-center py-4">
        No payment history found.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {receipts.map((receipt) => (
        <div 
          key={receipt.transaction_id}
          className="flex items-center justify-between p-3 bg-secondary/50 rounded-sm"
        >
          <div>
            <p className="font-medium">${receipt.amount?.toFixed(2)} CAD</p>
            <p className="text-xs text-muted-foreground">
              {new Date(receipt.created_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              })}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="rounded-sm"
            onClick={() => handleDownload(receipt.transaction_id)}
            disabled={downloadingId === receipt.transaction_id}
            data-testid={`download-receipt-${receipt.transaction_id}`}
          >
            {downloadingId === receipt.transaction_id ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Download className="w-4 h-4 mr-1" />
                Download
              </>
            )}
          </Button>
        </div>
      ))}
    </div>
  );
}
